import { cloneDeep } from "es-toolkit"
import { defineStore } from "pinia"
import { ref, computed, type Ref } from "vue"
import { profileAbilitiesProcessed } from "~/utils/editProfile/profileAbilitiesProcessed.js"
import { timestamp } from "~/utils/datetime"
import { useProfile } from "@/composables/useProfile.js"
import { useCurrentAttackerAdHocStore } from "@/stores/currentAttackerAdHoc"
import { mergeDefaultsInToWeapon } from "~/utils/defaults/weapon"
import { useGlobalStore } from "@/stores/global"
import { useAttackContextStore } from "@/stores/attackContext"
import type { Profile, Modifier, Weapon } from "~/types"

// Define interfaces for computed properties
interface ModifierWithComputed extends Modifier {
  computed?: {
    sourceUnit: {
      id: string
      name: string
    }
  }
}

interface WeaponWithComputed extends Weapon {
  computed?: {
    modelTypeId: string
    unitId: string
  }
}

const { mergeDefaultsInToProfile, transformAdHocProfile } = useProfile()

export const useCurrentAttackerStore = defineStore(
  "currentAttacker",
  () => {
    // State
    const currentAttacker: Ref<Partial<Profile>> = ref({})

    // Getters
    const currentAttackerCompatibleWithAttackType = computed(() => {
      const attackContextStore = useAttackContextStore()
      if (typeof currentAttacker.value?.computed_roles !== "undefined") {
        if (attackContextStore.attackContext.attackType === "Melee") {
          return currentAttacker.value.computed_roles.includes(
            "Attacker: Melee"
          )
        }
        if (attackContextStore.attackContext.attackType === "Ranged") {
          return currentAttacker.value.computed_roles.includes(
            "Attacker: Ranged"
          )
        }
      }
      return false
    })

    const currentAttackerAbilitiesFlat = computed((): Modifier[] => {
      // A flat list of all attacker unit abilities.
      if (typeof currentAttacker.value.units === "undefined") {
        return []
      }
      const returnArray: ModifierWithComputed[] = []
      currentAttacker.value?.units?.forEach((unit) => {
        if (typeof unit.abilities === "undefined") {
          return
        }
        unit.abilities.forEach((ability) => {
          const abilityProcessed: ModifierWithComputed = cloneDeep(ability)
          abilityProcessed.computed = {
            sourceUnit: {
              id: unit.id,
              name: unit.name,
            },
          }
          returnArray.push(abilityProcessed)
        })
      })
      return returnArray
    })
    const currentAttackerAbilitiesFiltered = computed(() => {
      const attackContextStore = useAttackContextStore()
      // A flat list of all attacker unit abilities, filtered by profile role and attack type.
      const abilitiesProcessed = profileAbilitiesProcessed(
        cloneDeep(currentAttackerAbilitiesFlat.value)
      )

      return abilitiesProcessed.filter((ability) => {
        if (ability?.profileRole === "Attacker") {
          // Ability profile role matches the role it's currently being used in.
          if (!ability.attackType) {
            // Ability doesn't specify an attack type.
            return true
          }
          if (
            ability.attackType === attackContextStore.attackContext.attackType
          ) {
            // Ability does specify an attack type, and we're currently in that attack type.
            return true
          }
        }
        return false
      })
    })
    const currentAttackerAbilitiesSelectedProfile = computed((): Modifier[] => {
      // The current attacker's selected profile abilities (as stored in their units).
      if (currentAttackerAbilitiesFlat.value.length === 0) {
        return []
      }

      const abilitiesProcessed = profileAbilitiesProcessed(
        currentAttackerAbilitiesFlat.value
      )

      // Collect all selected abilities from each unit in the profile
      const allSelectedAbilities: string[] = []
      if (currentAttacker.value.units) {
        currentAttacker.value.units.forEach((unit) => {
          if (
            unit.abilities_selected &&
            Array.isArray(unit.abilities_selected)
          ) {
            allSelectedAbilities.push(...unit.abilities_selected)
          }
        })
      }

      return abilitiesProcessed.filter((ability) => {
        let idMatch = false
        for (const selectedId of allSelectedAbilities) {
          if (ability.id === selectedId) {
            idMatch = true
            break
          }
        }
        return idMatch
      })
    })
    const currentAttackerAbilitiesSelectedSim = computed((): Modifier[] => {
      // The current attacker's selected profile abilities (as stored for the current sim).
      if (currentAttackerAbilitiesFlat.value.length === 0) {
        return []
      }

      const abilitiesProcessed = profileAbilitiesProcessed(
        currentAttackerAbilitiesFlat.value
      )

      // Collect all selected abilities from each unit in the profile
      const allSelectedAbilities: string[] = []
      // Melee profile abilities.
      if (
        Array.isArray(
          currentAttacker.value.sim?.abilities_selected_attacker_melee
        ) &&
        currentAttacker.value.sim.abilities_selected_attacker_melee.length
      ) {
        currentAttacker.value.sim.abilities_selected_attacker_melee.forEach(
          (abilityId) => {
            allSelectedAbilities.push(abilityId)
          }
        )
      }
      // Ranged profile abilities.
      if (
        Array.isArray(
          currentAttacker.value.sim?.abilities_selected_attacker_ranged
        ) &&
        currentAttacker.value.sim.abilities_selected_attacker_ranged.length
      ) {
        currentAttacker.value.sim.abilities_selected_attacker_ranged.forEach(
          (abilityId) => {
            allSelectedAbilities.push(abilityId)
          }
        )
      }

      return abilitiesProcessed.filter((ability) => {
        let idMatch = false
        for (const selectedId of allSelectedAbilities) {
          if (ability.id === selectedId) {
            idMatch = true
            break
          }
        }
        return idMatch
      })
    })
    const currentAttackerAbilitiesSelectedProfileFiltered = computed(() => {
      const attackContextStore = useAttackContextStore()
      // The current attacker's selected profile abilities (as stored in their units), filtered by profile role and attack type.
      if (!currentAttackerAbilitiesFiltered.value.length) {
        return []
      }
      const abilitiesProcessed = profileAbilitiesProcessed(
        cloneDeep(currentAttackerAbilitiesSelectedProfile.value)
      )

      return abilitiesProcessed.filter((ability) => {
        if (ability?.profileRole === "Attacker") {
          // Ability profile role matches the role it's currently being used in.
          if (!ability.attackType) {
            // Ability doesn't specify an attack type.
            return true
          }
          if (
            ability.attackType === attackContextStore.attackContext.attackType
          ) {
            // Ability does specify an attack type, and we're currently in that attack type.
            return true
          }
        }
        return false
      })
    })
    const currentAttackerAbilitiesSelectedSimFiltered = computed(() => {
      const attackContextStore = useAttackContextStore()
      // The current attacker's selected profile abilities (as stored in their units), filtered by profile role and attack type.
      if (!currentAttackerAbilitiesFiltered.value.length) {
        return []
      }
      const abilitiesProcessed = profileAbilitiesProcessed(
        cloneDeep(currentAttackerAbilitiesSelectedSim.value)
      )

      return abilitiesProcessed.filter((ability) => {
        if (ability?.profileRole === "Attacker") {
          // Ability profile role matches the role it's currently being used in.
          if (!ability.attackType) {
            // Ability doesn't specify an attack type.
            return true
          }
          if (
            ability.attackType === attackContextStore.attackContext.attackType
          ) {
            // Ability does specify an attack type, and we're currently in that attack type.
            return true
          }
        }
        return false
      })
    })
    const currentAttackerWeaponsFlat = computed((): Weapon[] => {
      // A flat list of all attacker weapons.
      if (typeof currentAttacker.value.units === "undefined") {
        return []
      }
      const returnArray: WeaponWithComputed[] = []
      currentAttacker.value.units.forEach((unit) => {
        if (!unit.model_types) {
          return // Skip this unit if model_types is undefined
        }
        unit.model_types.forEach((modelType) => {
          if (typeof modelType.weapons !== "undefined") {
            modelType.weapons.forEach((weapon) => {
              // Add computed data for convenient access during a simulation.
              const weaponWithComputed: WeaponWithComputed = { ...weapon }
              weaponWithComputed.computed = {
                modelTypeId: modelType.id,
                unitId: unit.id,
              }
              returnArray.push(weaponWithComputed)
            })
          }
        })
      })
      return returnArray
    })
    const currentAttackerWeaponsFiltered = computed(() => {
      const attackContextStore = useAttackContextStore()
      // A flat list of all attacker weapons filtered by type.
      return currentAttackerWeaponsFlat.value.filter((weapon) => {
        return weapon.type === attackContextStore.attackContext.attackType
      })
    })
    const currentAttackerWeaponsSelected = computed((): Weapon[] => {
      // The current attacker's selected weapons.
      if (currentAttackerWeaponsFlat.value.length === 0) {
        return []
      }

      // TODO: Shouldn't have to do this here.
      if (!currentAttacker.value.sim) {
        return []
      }

      if (
        typeof currentAttacker.value.sim.weapons_selected_melee === "undefined"
      ) {
        currentAttacker.value.sim.weapons_selected_melee = []
      }
      if (
        typeof currentAttacker.value.sim.weapons_selected_ranged === "undefined"
      ) {
        currentAttacker.value.sim.weapons_selected_ranged = []
      }

      if (
        currentAttacker.value.sim.weapons_selected_melee.length === 0 &&
        currentAttacker.value.sim.weapons_selected_ranged.length === 0
      ) {
        return []
      }

      return cloneDeep(currentAttackerWeaponsFlat.value).filter((weapon) => {
        let idMatch = false
        for (const selectedIdMelee of currentAttacker.value.sim!
          .weapons_selected_melee) {
          if (weapon.id === selectedIdMelee) {
            idMatch = true
            break
          }
        }
        for (const selectedIdRanged of currentAttacker.value.sim!
          .weapons_selected_ranged) {
          if (weapon.id === selectedIdRanged) {
            idMatch = true
            break
          }
        }
        return idMatch
      })
    })
    const currentAttackerWeaponsSelectedFiltered = computed(() => {
      const attackContextStore = useAttackContextStore()
      // The current attacker's **selected** weapons, filtered by melee or ranged compatibility.
      if (attackContextStore.attackContext.attackType === "Melee") {
        return currentAttackerWeaponsSelected.value.filter(
          (weapon) => weapon.type === "Melee"
        )
      }
      if (attackContextStore.attackContext.attackType === "Ranged") {
        return currentAttackerWeaponsSelected.value.filter(
          (weapon) => weapon.type !== "Melee"
        )
      }
      return []
    })

    // These getters handle the difference between ad hoc & profile input.
    const getAttackerForSim = computed(() => {
      const globalStore = useGlobalStore()
      if (globalStore.profileUiAttacker === "adhoc") {
        // Return full profile using ad hoc profile as a base.
        const currentAttackerAdHocStore = useCurrentAttackerAdHocStore()
        return transformAdHocProfile(
          cloneDeep(currentAttackerAdHocStore.profile),
          "attacker"
        )
      }
      return currentAttacker.value
    })
    const getAttackerAbilitiesForSim = computed(() => {
      const globalStore = useGlobalStore()
      const attackContextStore = useAttackContextStore()
      if (globalStore.profileUiAttacker === "adhoc") {
        return []
      }

      if (attackContextStore.attackContext.attackType === "Melee") {
        const abilitiesSelected: ModifierWithComputed[] = []

        // Loop through selected ability IDs
        for (const abilityId of currentAttacker.value.sim
          ?.abilities_selected_attacker_melee || []) {
          // Search through all units in the current attacker profile
          for (const unit of currentAttacker.value.units || []) {
            // Check if the unit has an abilities array
            if (unit.abilities && Array.isArray(unit.abilities)) {
              // Find the matching ability by ID
              const matchingAbility = unit.abilities.find(
                (ability) => ability.id === abilityId
              )
              if (matchingAbility) {
                // Create a deep clone to break reactivity chain
                const abilityClone: ModifierWithComputed =
                  cloneDeep(matchingAbility)

                // Add computed data for convenient access during simulation
                abilityClone.computed = {
                  sourceUnit: {
                    id: unit.id,
                    name: unit.name,
                  },
                }

                abilitiesSelected.push(abilityClone)
              }
            }
          }
        }

        return abilitiesSelected
      }

      if (attackContextStore.attackContext.attackType === "Ranged") {
        const abilitiesSelected: ModifierWithComputed[] = []

        // Loop through selected ability IDs
        for (const abilityId of currentAttacker.value.sim
          ?.abilities_selected_attacker_ranged || []) {
          // Search through all units in the current attacker profile
          for (const unit of currentAttacker.value.units || []) {
            // Check if the unit has an abilities array
            if (unit.abilities && Array.isArray(unit.abilities)) {
              // Find the matching ability by ID
              const matchingAbility = unit.abilities.find(
                (ability) => ability.id === abilityId
              )
              if (matchingAbility) {
                // Create a deep clone to break reactivity chain
                // (proxies can't be cloned)
                const abilityClone: ModifierWithComputed =
                  cloneDeep(matchingAbility)

                // Add computed data for convenient access during simulation
                abilityClone.computed = {
                  sourceUnit: {
                    id: unit.id,
                    name: unit.name,
                  },
                }

                abilitiesSelected.push(abilityClone)
              }
            }
          }
        }

        return abilitiesSelected
      }

      return []
    })
    const getWeaponsForSim = computed(() => {
      const globalStore = useGlobalStore()
      if (globalStore.profileUiAttacker === "adhoc") {
        const currentAttackerAdHocStore = useCurrentAttackerAdHocStore()
        if (typeof currentAttackerAdHocStore.profile.weapons !== "undefined") {
          // Each weapon needs to be merged with the default weapon profile.
          return currentAttackerAdHocStore.profile.weapons.map((weapon) => {
            const weaponClone = cloneDeep(weapon)
            const weaponWithDefaults = {
              ...weaponClone,
              countPerUnit: 1,
              type: "Ranged" as const,
            }
            return mergeDefaultsInToWeapon(weaponWithDefaults)
          })
        }
        return []
      }
      return currentAttackerWeaponsSelectedFiltered.value
    })

    // Actions
    function setCurrentAttacker(profile: Profile | null) {
      // console.log("setCurrentAttacker()", profile)

      if (profile === null) {
        currentAttacker.value = {}
        return
      }

      let weaponId = null

      // Automatically select the only weapon for the current attack type if there is only one.
      if (profile && typeof profile?.weapons !== "undefined") {
        if (
          currentAttackerWeaponsSelectedFiltered.value.length === 0 &&
          currentAttackerWeaponsFiltered.value.length === 1
        ) {
          // The current attacker has no weapons for the current attack type,
          // AND they only have one weapon that is compatible with the current attack type.
          // Automatically mark it as selected.
          weaponId = currentAttackerWeaponsFiltered.value[0].id
          if (weaponId) {
            autoSelectOnlyWeaponForAttackType(weaponId)
          }
        }
      }

      currentAttacker.value = mergeDefaultsInToProfile(cloneDeep(profile))

      // Initialise *_selected_* arrays for simulation.
      currentAttacker.value.sim = {
        abilities_selected_attacker_melee:
          currentAttackerAbilitiesSelectedProfileFiltered.value.map(
            (ability) => ability.id
          ),
        abilities_selected_attacker_ranged:
          currentAttackerAbilitiesSelectedProfileFiltered.value.map(
            (ability) => ability.id
          ),
        weapons_selected_melee: cloneDeep(
          currentAttacker.value.weapons_selected_melee || []
        ),
        weapons_selected_ranged: cloneDeep(
          currentAttacker.value.weapons_selected_ranged || []
        ),
      }
    }

    async function setCurrentAttackerAbilitiesSelected(payload: {
      abilitiesData: string[]
      profileId: string
    }) {
      const user = useSupabaseUser()
      const selectedAbilitiesIds = payload.abilitiesData

      // If user is logged in, work directly with the database
      if (user.value) {
        try {
          const supabaseClient = useSupabaseClient()

          // Update the currentAttacker in the local store
          const attackContextStore = useAttackContextStore()
          if (!currentAttacker.value.sim) {
            currentAttacker.value.sim = {
              abilities_selected_attacker_melee: [],
              abilities_selected_attacker_ranged: [],
              weapons_selected_melee: [],
              weapons_selected_ranged: [],
            }
          }
          if (attackContextStore.attackContext.attackType === "Melee") {
            currentAttacker.value.sim.abilities_selected_attacker_melee = [
              ...selectedAbilitiesIds,
            ]
          } else {
            currentAttacker.value.sim.abilities_selected_attacker_ranged = [
              ...selectedAbilitiesIds,
            ]
          }

          // Create an update object
          const updateData = {
            abilities_selected: [...selectedAbilitiesIds],
            updated: timestamp(),
          }

          // Update the profile in the database
          const { error: updateError } = await supabaseClient
            .from("profiles")
            .update(updateData)
            .eq("id", payload.profileId)

          if (updateError) {
            console.error(
              "Error updating profile abilities selection:",
              updateError
            )
          }
        } catch (error) {
          console.error("Error in database operation:", error)
        }
      } else {
        // For logged-out users, use the local store
        const globalStore = useGlobalStore()
        const sourceProfile = globalStore.profiles.find((profile) => {
          return profile.id === payload.profileId
        })

        if (!sourceProfile) {
          console.error(`Profile with ID ${payload.profileId} not found`)
          return
        }

        // Update the sim abilities selection based on attack type
        const attackContextStore = useAttackContextStore()
        if (!currentAttacker.value.sim) {
          currentAttacker.value.sim = {
            abilities_selected_attacker_melee: [],
            abilities_selected_attacker_ranged: [],
            weapons_selected_melee: [],
            weapons_selected_ranged: [],
          }
        }
        if (attackContextStore.attackContext.attackType === "Melee") {
          currentAttacker.value.sim.abilities_selected_attacker_melee = [
            ...selectedAbilitiesIds,
          ]
        } else {
          currentAttacker.value.sim.abilities_selected_attacker_ranged = [
            ...selectedAbilitiesIds,
          ]
        }

        // Also update the profile's abilities_selected if it exists
        if (sourceProfile.abilities_selected !== undefined) {
          sourceProfile.abilities_selected = [...selectedAbilitiesIds]
        }
      }
    }

    function autoSelectOnlyWeaponForAttackType(weaponId: string) {
      const attackContextStore = useAttackContextStore()
      const globalStore = useGlobalStore()
      const attackType =
        attackContextStore.attackContext.attackType.toLowerCase()

      // Initialize weapons_selected arrays if they don't exist
      if (!currentAttacker.value.weapons_selected_melee) {
        currentAttacker.value.weapons_selected_melee = []
      }
      if (!currentAttacker.value.weapons_selected_ranged) {
        currentAttacker.value.weapons_selected_ranged = []
      }

      // Current attacker has no weapons for the current attack type selected AND
      // they only have one weapon that is compatible with the current attack type.
      // Automatically mark it as selected.
      if (attackType === "melee") {
        currentAttacker.value.weapons_selected_melee.push(weaponId)
      }
      if (attackType === "ranged") {
        currentAttacker.value.weapons_selected_ranged.push(weaponId)
      }

      // Find the source profile in the profiles array and apply the change there too.
      const sourceProfile = globalStore.profiles.find(
        (profile) => profile.id === currentAttacker.value.id
      )

      if (sourceProfile) {
        if (!sourceProfile.weapons_selected_melee) {
          sourceProfile.weapons_selected_melee = []
        }
        if (!sourceProfile.weapons_selected_ranged) {
          sourceProfile.weapons_selected_ranged = []
        }
        if (attackType === "melee") {
          sourceProfile.weapons_selected_melee.push(weaponId)
        }
        if (attackType === "ranged") {
          sourceProfile.weapons_selected_ranged.push(weaponId)
        }
      }
    }

    async function setCurrentAttackerWeaponsSelected(
      selectedWeaponsIds: string[]
    ) {
      const attackContextStore = useAttackContextStore()

      // Ensure sim object exists
      if (!currentAttacker.value.sim) {
        currentAttacker.value.sim = {
          abilities_selected_attacker_melee: [],
          abilities_selected_attacker_ranged: [],
          weapons_selected_melee: [],
          weapons_selected_ranged: [],
        }
      }

      // Update the relevant weapons_selected_* array in the local store's currentAttacker object.
      if (attackContextStore.attackContext.attackType === "Melee") {
        // Clear existing melee weapons
        currentAttacker.value.sim.weapons_selected_melee = []
        // Re-populate with the selected melee weapons
        selectedWeaponsIds.forEach((weaponId) => {
          currentAttacker.value.sim.weapons_selected_melee.push(weaponId)
        })
      }
      if (attackContextStore.attackContext.attackType === "Ranged") {
        // Clear existing ranged weapons
        currentAttacker.value.sim.weapons_selected_ranged = []
        // Re-populate with the selected ranged weapons
        selectedWeaponsIds.forEach((weaponId) => {
          currentAttacker.value.sim.weapons_selected_ranged.push(weaponId)
        })
      }
    }

    function initAbilitiesSelected() {
      // Ensure sim object exists
      if (!currentAttacker.value.sim) {
        return
      }

      const attackContextStore = useAttackContextStore()
      // Check if any selected abilities aren't in the current attacker's abilities
      const currentAbilityIds = currentAttackerAbilitiesFlat.value.map(
        (ability) => ability.id
      )
      if (attackContextStore.attackContext.attackType === "Melee") {
        if (!currentAttacker.value.sim.abilities_selected_attacker_melee) {
          currentAttacker.value.sim.abilities_selected_attacker_melee = []
        }
        const hasInvalidSelection =
          currentAttacker.value.sim.abilities_selected_attacker_melee.some(
            (id) => !currentAbilityIds.includes(id)
          )
        // Only update if there are invalid selections
        if (hasInvalidSelection) {
          currentAttacker.value.sim.abilities_selected_attacker_melee =
            currentAttackerAbilitiesSelectedSimFiltered.value.map(
              (ability) => ability.id
            )
        }
      }
      if (attackContextStore.attackContext.attackType === "Ranged") {
        if (!currentAttacker.value.sim.abilities_selected_attacker_ranged) {
          currentAttacker.value.sim.abilities_selected_attacker_ranged = []
        }
        const hasInvalidSelection =
          currentAttacker.value.sim.abilities_selected_attacker_ranged.some(
            (id) => !currentAbilityIds.includes(id)
          )
        // Only update if there are invalid selections
        if (hasInvalidSelection) {
          currentAttacker.value.sim.abilities_selected_attacker_ranged =
            currentAttackerAbilitiesSelectedSimFiltered.value.map(
              (ability) => ability.id
            )
        }
      }
    }

    return {
      // State
      currentAttacker,
      // Getters
      currentAttackerCompatibleWithAttackType,
      currentAttackerAbilitiesFlat,
      currentAttackerAbilitiesFiltered,
      currentAttackerAbilitiesSelectedProfile,
      currentAttackerAbilitiesSelectedSim,
      currentAttackerAbilitiesSelectedProfileFiltered,
      currentAttackerAbilitiesSelectedSimFiltered,
      currentAttackerWeaponsFlat,
      currentAttackerWeaponsFiltered,
      currentAttackerWeaponsSelected,
      currentAttackerWeaponsSelectedFiltered,
      getAttackerForSim,
      getAttackerAbilitiesForSim,
      getWeaponsForSim,
      // Actions
      setCurrentAttacker,
      setCurrentAttackerAbilitiesSelected,
      autoSelectOnlyWeaponForAttackType,
      setCurrentAttackerWeaponsSelected,
      initAbilitiesSelected,
    }
  },
  {
    persist: {
      key: "unitcrunch-currentAttacker",
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ["currentAttacker"],
    },
  }
)
